import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import {
  Phone,
  MessageCircle,
  Mail,
  CheckCircle2,
  ArrowRight,
  Clock,
  Globe2,
  MapPin,
  ShieldCheck,
  Star,
} from "lucide-react";
import { SERVICES, type ServiceId } from "@/data/services";
import { SERVICE_HEAD, resolveServiceIdLight } from "@/data/services-head.gen";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { getReviewerForService, attorneyPersonSchema } from "@/data/service-reviewers";

const INCLUDED = [
  "Free quote within 15 minutes",
  "PDF preview before printing",
  "QC by senior reviewer",
  "Local Bangkok delivery same-day",
  "DHL / FedEx tracking worldwide",
  "Lifetime re-issue if lost",
];

const WHY = [
  {
    icon: ShieldCheck,
    t: "Officially registered",
    d: "MFA-listed translator · Thai Lawyers Council Notary Public",
  },
  { icon: Globe2, t: "50+ languages in-house", d: "Native reviewers for every language pair" },
  { icon: Clock, t: "Reply in 15 min", d: "Mon–Sat 09:00–18:00 ICT, LINE on-call after hours" },
  {
    icon: Star,
    t: "6 notarial attorneys",
    d: "15+ years serving embassies, Fortune 500, and individuals",
  },
];

const routeApi = getRouteApi("/en/services/$service/");

export function EnServiceDetailPage() {
  const { service } = routeApi.useLoaderData() as { service: ServiceId };
  const s = SERVICES[service];

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      {/* Hero */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <nav className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">
              EN home
            </Link>
            <span className="text-white/30">/</span>
            <Link to="/en/services" className="text-amber-300 hover:underline">
              Services
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/80">{s.nameEn}</span>
            <span className="text-white/30">·</span>
            <Link
              to="/services/$service"
              params={{ service: s.slug }}
              className="text-white/80 hover:text-amber-300"
            >
              ภาษาไทย
            </Link>
          </nav>

          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">{s.nameEn}</h1>
          <p className="mt-4 max-w-3xl text-base text-white/80 sm:text-lg">{s.taglineEn}</p>

          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 font-semibold text-amber-200">
              From ฿{s.priceFrom.toLocaleString()}
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80">
              <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
              {s.turnaround}
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80">
              <Globe2 className="-mt-0.5 mr-1 inline h-3 w-3" />
              {s.languagesCount}+ languages
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80">
              <MapPin className="-mt-0.5 mr-1 inline h-3 w-3" />
              {s.coverage}
            </span>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="tel:+66832494999"
              data-track-zone="en-service-hero"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep shadow-xl shadow-amber-500/30 hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> Call +66 83 249 4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noopener noreferrer"
              data-track-zone="en-service-hero"
              className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]"
            >
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  data-track-zone="en-service-hero"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white backdrop-blur hover:bg-white/10"
                >
                  <Mail className="h-4 w-4" /> Free Quote
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* Pricing table */}
      {s.prices.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold">Transparent Pricing</h2>
            <p className="mt-2 text-sm text-white/80">
              Published rates. Bulk and enterprise discounts on request.
            </p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <table className="w-full text-sm">
                <tbody>
                  {s.prices.map((p, i) => (
                    <tr key={p.item + i} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                      <td className="px-6 py-3 text-white/85">{p.item}</td>
                      <td className="px-4 py-3 text-right font-bold text-amber-300">{p.price}</td>
                      {p.note && (
                        <td className="px-6 py-3 text-right text-xs text-white/80">{p.note}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* What's included */}
      <section className="border-t border-white/10 bg-[#0d2150] px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">What's Always Included</h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {INCLUDED.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white/85"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why us */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">Why International Clients Choose NYC</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w) => (
              <article key={w.t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <w.icon className="h-6 w-6 text-amber-300" />
                <h3 className="mt-3 text-sm font-bold">{w.t}</h3>
                <p className="mt-1 text-xs text-white/80">{w.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      {s.relatedServiceIds.length > 0 && (
        <section className="border-t border-white/10 px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold">Related Services</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {s.relatedServiceIds.slice(0, 6).map((rid) => {
                const r = SERVICES[rid];
                if (!r) return null;
                return (
                  <Link
                    key={rid}
                    to="/en/services/$service"
                    params={{ service: r.slug }}
                    className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-amber-400/40 hover:bg-white/[0.06]"
                  >
                    <h3 className="text-sm font-bold">{r.nameEn}</h3>
                    <p className="mt-1 line-clamp-2 text-xs text-white/80">{r.taglineEn}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-300 group-hover:gap-2">
                      {`Learn about ${r.nameEn}`} <ArrowRight className="h-3 w-3" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Need it today? We deliver in 4 hours.
          </h2>
          <p className="mt-3 text-white/75">
            Same-day rush, embassy hand-delivery, and international DHL — all under one roof.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="tel:+66832494999"
              data-track-zone="en-service-footer"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a
              href="mailto:contact@nyclegal.co.th"
              data-track-zone="en-service-footer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Mail className="h-4 w-4" /> contact@nyclegal.co.th
            </a>
          </div>
          <p className="mt-6 text-xs text-white/80">
            Full Thai content with {s.faqs.length}+ FAQs:{" "}
            <Link
              to="/services/$service"
              params={{ service: s.slug }}
              className="underline hover:text-amber-300"
            >
              {s.nameTh}
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}
